import { useMemo } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
} from '../../components/DataTablePanel';
import { Card } from '../../components/ui';
import { t } from '../../i18n';
import type { MydataPeriodSummary } from '../../types/mydata';
import { formatDate, formatDateTime, formatMoney } from '../../utils/format';
import { vatCodeSide, type VatCodeSide } from './vatCodes';

function toNum(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function FormRow({
  label,
  value,
  emphasize,
  muted,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 border-b border-line py-2.5 last:border-b-0 ${
        emphasize ? 'pt-3' : ''
      }`}
    >
      <span
        className={`text-sm ${
          emphasize
            ? 'font-semibold text-ink'
            : 'text-ink-muted'
        }`}
      >
        {label}
      </span>
      <span
        className={`shrink-0 tabular-nums text-sm ${
          emphasize
            ? 'text-lg font-semibold text-ink'
            : muted
              ? 'text-ink-faint'
              : 'font-medium text-ink'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

type VatCodeRow = { code: string; amount: number; side: VatCodeSide };

function compareVatCodes(a: VatCodeRow, b: VatCodeRow) {
  const na = Number(a.code);
  const nb = Number(b.code);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return a.code.localeCompare(b.code, 'el');
}

export function MydataDebtsForm({
  summary,
}: {
  summary: MydataPeriodSummary | null | undefined;
}) {
  const labels = t().mydata;
  const vatPayable = toNum(summary?.vatPayable);
  const isRefund = vatPayable < 0;
  const resultLabel = isRefund ? labels.vatRefundable : labels.vatPayable;
  const resultAmount = formatMoney(Math.abs(vatPayable));

  const deltaNum =
    summary?.previous?.vatPayableDelta != null
      ? toNum(summary.previous.vatPayableDelta)
      : null;
  const deltaLabel =
    deltaNum == null
      ? null
      : `${deltaNum > 0 ? '+' : ''}${formatMoney(deltaNum)}`;

  const vatCodeSections = useMemo(() => {
    const entries: VatCodeRow[] = Object.entries(summary?.vatCodes ?? {})
      .map(([code, amount]) => ({
        code: String(code),
        side: vatCodeSide(String(code)),
        amount: Number(amount) || 0,
      }))
      .filter((row) => row.amount !== 0)
      .sort(compareVatCodes);

    const bySide = (side: VatCodeSide) =>
      entries.filter((row) => row.side === side);

    return {
      sales: bySide('sales'),
      purchases: bySide('purchases'),
      adjustments: bySide('adjustment'),
      other: bySide('other'),
    };
  }, [summary?.vatCodes]);

  const hasVatCodes =
    vatCodeSections.sales.length > 0 ||
    vatCodeSections.purchases.length > 0 ||
    vatCodeSections.adjustments.length > 0 ||
    vatCodeSections.other.length > 0;

  const periodLabel =
    summary?.dateFrom && summary?.dateTo
      ? `${formatDate(summary.dateFrom)} – ${formatDate(summary.dateTo)}`
      : '—';

  function renderCodeTable(
    rows: VatCodeRow[],
    title: string,
    hint: string,
  ) {
    if (rows.length === 0) return null;
    const total = rows.reduce((acc, row) => acc + row.amount, 0);
    return (
      <Card className="space-y-3 p-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink">{title}</h3>
            <span className="text-sm font-semibold tabular-nums text-ink">
              {formatMoney(total)}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>
        </div>
        <DataTablePanel>
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.vatCode}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.vatCodeDescription}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.debtsAmount}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.code} className="border-t border-line">
                  <td className="px-3 py-2 font-medium tabular-nums text-ink">
                    {row.code}
                  </td>
                  <td className="px-3 py-2 text-ink-muted">
                    {labels.vatCodeLabels[row.code] ??
                      `${labels.vatCode} ${row.code}`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">
                    {formatMoney(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTablePanel>
      </Card>
    );
  }

  return (
    <section className="shrink-0 space-y-4">
      <Card className="p-4">
        <div className="mb-3 space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-xs font-medium uppercase tracking-wide text-ink-faint">
              {labels.debtsPeriod}
            </div>
            <div className="text-sm tabular-nums text-ink-muted">
              {periodLabel}
            </div>
          </div>
          {summary?.syncedAt ? (
            <div className="text-xs text-ink-faint">
              {labels.freshFromAade} · {formatDateTime(summary.syncedAt)}
            </div>
          ) : null}
        </div>

        <p className="mb-3 text-xs text-ink-faint">{labels.debtsFormula}</p>

        <div className="rounded-lg border border-line bg-surface px-3">
          <FormRow
            label={labels.outputVat}
            value={formatMoney(summary?.outputVat ?? '0.00')}
          />
          <FormRow
            label={`− ${labels.inputVat}`}
            value={formatMoney(summary?.inputVat ?? '0.00')}
            muted
          />
          <FormRow label={resultLabel} value={resultAmount} emphasize />
        </div>

        {deltaLabel ? (
          <div className="mt-3 flex items-center justify-between gap-3 text-xs">
            <span className="text-ink-faint">
              {labels.vsPrevious} ({labels.delta})
            </span>
            <span
              className={`tabular-nums ${
                deltaNum != null && deltaNum > 0
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : deltaNum != null && deltaNum < 0
                    ? 'text-rose-700 dark:text-rose-400'
                    : 'text-ink-faint'
              }`}
            >
              {deltaLabel}
            </span>
          </div>
        ) : null}

        <p className="mt-3 text-xs text-ink-faint">
          {isRefund ? labels.vatRefundableHint : labels.vatPayableHint}
        </p>
      </Card>

      <div className="space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-ink">
            {labels.chartVatCodesTitle}
          </h3>
          <p className="text-xs leading-relaxed text-ink-faint">
            {labels.chartVatCodesHint}
          </p>
        </div>

        {!hasVatCodes ? (
          <p className="text-sm text-ink-faint">{labels.debtsNoCodes}</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {renderCodeTable(
              vatCodeSections.sales,
              labels.vatCodesSalesTitle,
              labels.vatCodesSalesHint,
            )}
            {renderCodeTable(
              vatCodeSections.purchases,
              labels.vatCodesPurchasesTitle,
              labels.vatCodesPurchasesHint,
            )}
            {renderCodeTable(
              vatCodeSections.adjustments,
              labels.vatCodesAdjustmentsTitle,
              labels.vatCodesAdjustmentsHint,
            )}
            {renderCodeTable(
              vatCodeSections.other,
              labels.vatCodesOtherTitle,
              labels.vatCodesOtherHint,
            )}
          </div>
        )}
      </div>
    </section>
  );
}
